import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import InputView from '../InputView'
import { ChangeEventHandler, useState } from 'react'
import FormPage from '../../../Components/Form/Page/FormPage'
import { Input } from '../../../Components/Form/Inputs'
import './BudgetInput.less'
import isBizBrief from '../../../Logic/Form/Sequences/Biz/isBizBrief'
import { BudgetGuidanceType } from './BudgetGuidance'
import BizBudgetOptions from './Biz/BudgetOptions'
import LFBudgetOptions from './LF/BudgetOptions'
import Select from '../../../Components/Form/Inputs/Select'
import { Currency, SupportedCurrencyCode } from '../../../Types/generated-types'

const MAXIMUM_BUDGET = 9000000

interface CurrencySelectorProps extends Partial<HTMLSelectElement> {
  setValue: (val: SupportedCurrencyCode) => void
  currencies: Currency[]
}

const ArrowIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox='2 2 19.2 19.2' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M6.98438 9.98438H17.0156L12 15L6.98438 9.98438Z' fill='black' fillOpacity='0.54' />
  </svg>
)

const CurrencySelector = ({ id, value, setValue, required, currencies }: CurrencySelectorProps) => {
  const onChange: ChangeEventHandler<HTMLSelectElement> = ({ target }) =>
    setValue(target.value as SupportedCurrencyCode)
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      required={required}
      className={`currency-selector-input ${value === '' && 'no-selection'}`}
      icon={<ArrowIcon className='clock-icon' aria-hidden='true' />}>
      {currencies.map((currency) => (
        <option key={currency.code} value={currency.code}>
          {currency.code} {currency.symbol}
        </option>
      ))}
    </Select>
  )
}

const BudgetInput: InputView = ({ path }) => {
  const { brief, supportedCurrencies } = useBriefContext()
  const [briefCurrency, setBriefCurrency] = useState<SupportedCurrencyCode | undefined>(
    brief.currency
  )
  const [budget, setBudget] = useState<number | ''>(brief.budget ?? '')
  const [budgetNotes, setBudgetNotes] = useState<string | ''>(brief.budgetNotes ?? '')

  const [budgetGuidance, setBudgetGuidance] = useState<BudgetGuidanceType>({
    budgetGuidanceBasic: brief.budgetGuidanceBasic,
    budgetGuidanceAverage: brief.budgetGuidanceAverage,
    budgetGuidanceHighEnd: brief.budgetGuidanceHighEnd,
  })

  const onChangeBudget: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setBudget(target.value && parseInt(target.value, 10))
  }

  const allAttributes = {
    currency: briefCurrency,
    budget: budget === '' ? undefined : Math.min(budget, MAXIMUM_BUDGET),
    budgetNotes: budgetNotes,
    budgetGuidanceBasic: budgetGuidance.budgetGuidanceBasic,
    budgetGuidanceAverage: budgetGuidance.budgetGuidanceAverage,
    budgetGuidanceHighEnd: budgetGuidance.budgetGuidanceHighEnd,
  }

  const currency = supportedCurrencies.find((c) => c.code === briefCurrency)

  return (
    <FormPage
      metaTitle='Event budget - HeadBox'
      heading="What's the budget for your event?"
      path={path}
      inputAttributes={allAttributes}
      className='budget-page'>
      <div>
        {isBizBrief(brief) && (
          <span className='input-wrapper'>
            <label htmlFor='currency-select' className='visually-hidden'>
              Select currency for event
            </label>
            <CurrencySelector
              id='currency-select'
              currencies={supportedCurrencies}
              value={briefCurrency}
              setValue={setBriefCurrency}
            />
          </span>
        )}
        <span className='input-wrapper'>
          <label htmlFor='budget-input' className='visually-hidden'>
            Event budget in {`${currency?.symbol} `}
          </label>
          <em aria-hidden='true'>{currency?.symbol}</em>
          <Input
            id='budget-input'
            type='number'
            value={budget}
            onChange={onChangeBudget}
            fieldName='budget'
            max={MAXIMUM_BUDGET}
            min={1}
            step={1}
            required
          />
        </span>
      </div>

      <br />

      {isBizBrief(brief) ? (
        <BizBudgetOptions budget={budget} currency={currency} />
      ) : (
        <LFBudgetOptions
          budget={budget}
          currency={currency}
          setBudgetGuidance={setBudgetGuidance}
          budgetNotes={budgetNotes}
          setBudgetNotes={setBudgetNotes}
        />
      )}
    </FormPage>
  )
}

export default BudgetInput
